
import Vue from 'vue'
import store from '../store';
import { tdApi } from "../services/td/td.api.service";
import { IContainer } from "@/shared/models/container.model";
import { INode } from "@/shared/models/node.model";
import GuidelineBox from '../components/GuidelineBox.vue';
import SiteFooter from '../components/SiteFooter.vue';
import IntroductionText from '../components/IntroductionText.vue';
import { HtmlHelper } from "@/shared/lib/htmlhelper";
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

export default Vue.extend({
  name: 'ExportView',
  components: {
    GuidelineBox,
    SiteFooter,
    IntroductionText
  },
  data() {
    return {
      exportContent: [] as IContainer[],
      localCart: [] as INode[],
      nodeIds: [] as number[],
      snackbar: false as boolean,
      snackbarMessage: 'Länken är kopierad' as string,
      isLoaded: false as boolean,
      showAll: false as boolean,
      chapters: [] as INode[],
      pdfGenerating: false as boolean
    };
  },
  async mounted() {
    await this.exportCart();
    this.isLoaded = true;
    
    // Check if PDF export was requested
    if (window.location.search.includes("pdf=true")) {
      // Wait a bit for DOM to render
      setTimeout(() => {
        this.generatePDF();
      }, 1000);
    }
  },

  methods: {
    async exportCart(): Promise<void> {
      this.nodeIds = [] as number[];
      if (window.location.search.includes("?nodes=")) {
        // Extract nodes parameter and remove any other query params like &pdf=true
        const urlParams = new URLSearchParams(window.location.search);
        const nodesParam = urlParams.get('nodes');
        if (nodesParam) {
          this.nodeIds = nodesParam.split(",").map(id => Number(id));
          
          // Use for...of to properly await each async call
          for (const nodeId of this.nodeIds) {
            const localNode = await tdApi.getSelectableNodeById(nodeId.toString());
            localNode.Chapter = Number(localNode.BreadCrumb[0].split(".")[0]) as number;
            this.localCart.push(localNode);
          }
          
          // Sort after all nodes are loaded
          this.localCart = this.localCart.sort(function (a, b) {
            if (a.Chapter < b.Chapter) { return -1; }
            else if (a.Chapter > b.Chapter) { return 1; }
            else {
              if (a.SortOrder < b.SortOrder) {
                return -1;
              }
              else if (a.SortOrder > b.SortOrder) {
                return 1;
              }
              else return 0;
            }
          });
        }
      } else {
        store.state.cart.forEach(element => {
          this.nodeIds.push(element.Id);
        });
        this.localCart = store.state.cart.map(item => ({
          Id: item.Id,
          Chapter: 0,
          Name: '',
          Selectable: false,
          BreadCrumb: [],
          SortOrder: 0,
          Url: item.Url,
          ParentId: 0
        })) as any;
      }
      this.exportContent = await tdApi.getConditionsForExport(this.nodeIds);
    },
    getBreadCrumb(items: Array<string>): string {
      return items.join(" / ");
    },
    getExportItem(id: number): IContainer {
      return this.exportContent.filter(item => item.Id === id)[0];
    },
    print(): void {
      window.print();
    },
    async share(): Promise<void> {
      const helper = new HtmlHelper();
      const url = helper.getBaseUrl() + "/export/?nodes=" + this.nodeIds.join(",");
      let successfull = await navigator.clipboard.writeText(url).then(function () {
        return true;
      }, function (err) {
        return false;
      });
      if (successfull) {
        this.snackbarMessage = 'Länken är kopierad';
        this.snackbar = true;
      }
    },
    async generatePDF(): Promise<void> {
      try {
        this.pdfGenerating = true;
        
        // Get the export container
        const element = document.querySelector('.export') as HTMLElement;
        if (!element) {
          // Export container not found
          return;
        }

        // Create canvas from HTML
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        // Set PDF metadata for accessibility
        pdf.setProperties({
          title: 'Riktlinjer Export',
          subject: 'Exporterade riktlinjer från kundvagnen',
          author: 'VGR Riktlinjer',
          keywords: 'riktlinjer, export, pdf',
          creator: 'VGR Riktlinjer System'
        });

        // Calculate dimensions
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        // Add first page
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, '', 'FAST');
        heightLeft -= pageHeight;

        // Add additional pages if needed
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, '', 'FAST');
          heightLeft -= pageHeight;
        }

        // Generate filename with timestamp
        const now = new Date();
        const filename = `riktlinjer-export-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
        
        // Save the PDF
        pdf.save(filename);
        
        this.snackbarMessage = 'PDF har skapats och laddats ner';
        this.snackbar = true;
      } catch (error) {
        // Error generating PDF
        this.snackbarMessage = 'Ett fel uppstod vid generering av PDF';
        this.snackbar = true;
      } finally {
        this.pdfGenerating = false;
      }
    },
    back(): void {
      window.history.back();
    },
    async getAll(): Promise<void> {
      this.showAll = true;
      this.isLoaded = false;
      const tree = await tdApi.getTree();
      let baseNode = {} as INode;
      baseNode.Children = tree;

      this.localCart = this.getLeafNodes(baseNode);

      tree.forEach(element => {
        this.chapters.push(element);
      });

      let idList: number[] = this.localCart.map((obj) => obj.Id);

      const chunkedIdList: number[][] = [];
      for (let i = 0; i < idList.length; i += 10) {
        chunkedIdList.push(idList.slice(i, i + 10));
      }
      this.exportContent = [];
      for (const chunk of chunkedIdList) {
        let conditions = await tdApi.getConditionsForExport(chunk);
        this.exportContent.push(...conditions);
      }

      idList = this.chapters.map((obj) => obj.Id);
      let conditions = await tdApi.getConditionsForExport(idList);
      this.exportContent.push(...conditions);

      this.isLoaded = true;
    },

    getLeafNodes<T>(node: INode, result: INode[] = []): INode[] {
      if (!node.Children || node.Children.length === 0) {
        if (node.ParentId !== undefined) {
          result.push(node);
        }
      } else {
        for (const child of node.Children) {
          this.getLeafNodes(child, result);
        }
      }
      return result;
    },
    async getConditions(id: number): Promise<IContainer> {
      return await tdApi.getConditions(id);
    },
  },
})
